/**
 * rf.js — pure React Flow data mapper.
 *
 * toReactFlow(elements, positions) → { nodes, edges }
 *
 * Vertex label is the joined labels array ("unlabeled" when empty) — never the element_id, which is
 * meaningless to users. Edge label is the label field. Per-prop label overrides were removed (deferred to a
 * future per-type rendering framework).
 *
 * Each edge also carries parallelIndex/parallelCount (its position among the edges that share the same
 * unordered vertex pair) so the edge renderer fans out ONLY genuinely parallel edges and leaves a lone edge
 * connected straight to its endpoints.
 */

// Unordered pair key so A→B and B→A are grouped together for fan-out.
function pairKey(el) {
  return el.from_id < el.to_id ? `${el.from_id}|${el.to_id}` : `${el.to_id}|${el.from_id}`;
}

export function toReactFlow(elements, positions) {
  const vertices = elements.filter(el => el.type === 'vertex');
  const edgeElements = elements.filter(el => el.type === 'edge');
  const vertexIds = new Set(vertices.map(v => v.element_id));

  const nodes = vertices.map(el => {
    const p = positions[el.element_id] || { x: 0, y: 0 };
    return {
      id: el.element_id,
      type: 'graphVertex',
      position: { x: p.x, y: p.y },
      data: { element: el, label: resolveVertexLabel(el) },
    };
  });

  // Keep only edges whose endpoints exist, then count how many share each vertex pair.
  const validEdges = edgeElements.filter(el => vertexIds.has(el.from_id) && vertexIds.has(el.to_id));
  const pairCounts = {};
  for (const el of validEdges) {
    const k = pairKey(el);
    pairCounts[k] = (pairCounts[k] || 0) + 1;
  }

  const pairSeen = {};
  const edges = validEdges.map(el => {
    const k = pairKey(el);
    const parallelIndex = pairSeen[k] || 0;
    pairSeen[k] = parallelIndex + 1;
    return {
      id: el.element_id,
      type: 'floating',
      source: el.from_id,
      target: el.to_id,
      animated: true,
      data: {
        element: el,
        label: resolveEdgeLabel(el),
        parallelIndex,
        parallelCount: pairCounts[k],
      },
    };
  });

  return { nodes, edges };
}

// Vertex display string: joined labels, or "unlabeled". Never the element_id.
function resolveVertexLabel(el) {
  if (Array.isArray(el.labels) && el.labels.length > 0) {
    return el.labels.join(', ');
  }
  return 'unlabeled';
}

// Edge display string: the label field.
function resolveEdgeLabel(el) {
  return el.label;
}
